import React, { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { DumbbellIcon, ScaleIcon, ZapIcon, HeartIcon, CheckCircleIcon, ArrowRightIcon, ArrowLeftIcon, CheckIcon, ShoppingCartIcon } from 'lucide-react';
type Goal = 'muscle' | 'weight' | 'performance' | 'health';
interface Supplement {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  goals: Goal[];
}
const mockSupplements: Supplement[] = [{
  id: '1',
  name: '100% Whey Protein Isolate',
  description: 'Fast-absorbing protein for muscle recovery',
  price: 249,
  image: 'https://drnutrition.com/storage/media/dnXydbj2uuEivTxr3oVA80E9Z07y2yoOeiJTpnsE.jpg',
  goals: ['muscle', 'performance']
}, {
  id: '2',
  name: 'Creatine Monohydrate',
  description: 'Increase strength and power output',
  price: 129,
  image: 'https://drnutrition.com/storage/media/prlvW98QxVHRvalrZxM4NppiQeglKJpljsFMdDGz.jpg',
  goals: ['muscle', 'performance']
}, {
  id: '3',
  name: 'Thermogenic Fat Burner',
  description: 'Boost metabolism and energy',
  price: 189,
  image: 'https://drnutrition.com/storage/media/uksBiPpW7gAOFEDeDTmu50CbihxIWLQKjiOeSqtu.jpg',
  goals: ['weight']
}, {
  id: '4',
  name: 'L-Carnitine Liquid',
  description: 'Convert fat into energy',
  price: 149,
  image: 'https://drnutrition.com/storage/media/dnXydbj2uuEivTxr3oVA80E9Z07y2yoOeiJTpnsE.jpg',
  goals: ['weight', 'performance']
}, {
  id: '5',
  name: 'Pre-Workout Explosive',
  description: 'Intense energy and focus',
  price: 199,
  image: 'https://drnutrition.com/storage/media/prlvW98QxVHRvalrZxM4NppiQeglKJpljsFMdDGz.jpg',
  goals: ['performance', 'muscle']
}, {
  id: '6',
  name: 'Daily Multivitamin',
  description: 'Essential vitamins and minerals',
  price: 99,
  image: 'https://drnutrition.com/storage/media/uksBiPpW7gAOFEDeDTmu50CbihxIWLQKjiOeSqtu.jpg',
  goals: ['health', 'muscle', 'weight', 'performance']
}, {
  id: '7',
  name: 'Omega-3 Fish Oil',
  description: 'Heart and joint health support',
  price: 119,
  image: 'https://drnutrition.com/storage/media/dnXydbj2uuEivTxr3oVA80E9Z07y2yoOeiJTpnsE.jpg',
  goals: ['health', 'weight']
}, {
  id: '8',
  name: 'BCAA Recovery',
  description: 'Intra-workout muscle preservation',
  price: 159,
  image: 'https://drnutrition.com/storage/media/prlvW98QxVHRvalrZxM4NppiQeglKJpljsFMdDGz.jpg',
  goals: ['muscle', 'performance']
}];
export function BundleBuilderPage() {
  const [step, setStep] = useState(1);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [selectedSupplements, setSelectedSupplements] = useState<string[]>([]);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);
  const goals = [{
    id: 'muscle' as Goal,
    title: 'Muscle Building',
    description: 'Gain size and strength',
    icon: <DumbbellIcon className="w-8 h-8" data-id="element-6315" data-mp="3hqvbe3s527s3" />,
    emoji: '💪'
  }, {
    id: 'weight' as Goal,
    title: 'Weight Loss',
    description: 'Burn fat and get lean',
    icon: <ScaleIcon className="w-8 h-8" data-id="element-6316" data-mp="1p3gyoqm2en9g" />,
    emoji: '🔥'
  }, {
    id: 'performance' as Goal,
    title: 'Performance',
    description: 'Boost energy and endurance',
    icon: <ZapIcon className="w-8 h-8" data-id="element-6317" data-mp="1g2isq2s2g5o8" />,
    emoji: '⚡'
  }, {
    id: 'health' as Goal,
    title: 'General Health',
    description: 'Overall wellness and immunity',
    icon: <HeartIcon className="w-8 h-8" data-id="element-6318" data-mp="2cum9ush28rdf" />,
    emoji: '❤️'
  }];
  const filteredSupplements = mockSupplements.filter(s => selectedGoal ? s.goals.includes(selectedGoal) : true);
  const toggleSupplement = (id: string) => {
    setSelectedSupplements(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };
  const originalTotal = selectedSupplements.reduce((total, id) => {
    const supp = mockSupplements.find(s => s.id === id);
    return total + (supp?.price || 0);
  }, 0);
  const savings = originalTotal * 0.2;
  const discountedTotal = originalTotal - savings;
  const handleAddToCart = () => {
    toast.success('Bundle added to cart successfully!', {
      description: `You saved SAR ${savings.toFixed(2)} on this bundle.`,
      icon: <CheckCircleIcon className="w-5 h-5 text-green-500" data-id="element-6319" data-mp="mcxvwgoxeel3" />
    });
    setStep(1);
    setSelectedGoal(null);
    setSelectedSupplements([]);
  };
  return <HelmetProvider data-id="element-6320" data-mp="w7punnu8v7hg">
      <div className="min-h-screen bg-gray-50 flex flex-col font-sans" data-id="element-6321" data-mp="w89a8xwf9jsb">
        <SEO title="Bundle Builder | Innova Pharmacy" description="Build your custom supplement bundle and save 20%." data-id="element-6322" data-mp="116u0q5lvgp3k" />

        <div className="hidden md:block" data-id="element-6323" data-mp="160y8us6vj368">
          <AnnouncementBar data-id="element-6324" data-mp="1a0fhtvlieig5" />
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-6325" data-mp="19znwrepjg344" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-6326" data-mp="1jxtxvu3it57x" />
        </div>

        <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-6327" data-mp="3b486ienfybsu">
          <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} alwaysWhiteBackground={true} data-id="element-6328" data-mp="3pscpzkubirco" />
        </div>

        <div className="pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-6329" data-mp="2lahwrcixkfu">
          <PageHeroBreadcrumb title="Bundle Builder" description="Build your custom supplement bundle and save 20%." breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Bundle Builder'
        }]} data-id="element-6330" data-mp="3upmf0zdsp0zz" />
        </div>

        <main className="flex-grow container mx-auto px-4 py-8 max-w-4xl" data-id="element-6331" data-mp="29dupx25ow3c8">
          {/* Progress Bar */}
          <div className="mb-8" data-id="element-6332" data-mp="our008l8jnse">
            <div className="flex justify-between mb-2" data-id="element-6333" data-mp="ovsk12k8bkm9">
              <span className={`text-sm font-medium ${step >= 1 ? 'text-[#1E2B55]' : 'text-gray-400'}`} data-id="element-6334" data-mp="tud9q81uulwg">
                1. Goal
              </span>
              <span className={`text-sm font-medium ${step >= 2 ? 'text-[#1E2B55]' : 'text-gray-400'}`} data-id="element-6335" data-mp="k40u8u8wdix6">
                2. Supplements
              </span>
              <span className={`text-sm font-medium ${step >= 3 ? 'text-[#1E2B55]' : 'text-gray-400'}`} data-id="element-6336" data-mp="b3m49hkznfnc">
                3. Review
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-6337" data-mp="15g69b2aytnr">
              <motion.div className="h-full bg-[#1E2B55]" initial={{
              width: '33%'
            }} animate={{
              width: `${step / 3 * 100}%`
            }} transition={{
              duration: 0.3
            }} data-id="element-6338" data-mp="17irziwfa0mo" />
            </div>
          </div>

          <AnimatePresence mode="wait" data-id="element-6339" data-mp="ebj2yz1no2qc">
            {step === 1 && <motion.div key="step1" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="space-y-6" data-id="element-6340" data-mp="9jixkr2tghmd">
                <div className="text-center mb-8" data-id="element-6341" data-mp="3mrvqw0o0gk76">
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-6342" data-mp="3msnkx71ym9vc">
                    Choose Your Goal
                  </h2>
                  <p className="text-gray-600" data-id="element-6343" data-mp="1kyekt1fj3rej">
                    Select your primary fitness goal to see recommended
                    supplements.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-6344" data-mp="1niugb14u5x2w">
                  {goals.map(goal => <button key={goal.id} onClick={() => {
                setSelectedGoal(goal.id);
                setStep(2);
              }} className={`p-6 rounded-2xl border-2 text-left transition-all duration-200 ${selectedGoal === goal.id ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:border-green-200 hover:shadow-md'}`} data-id="element-6345" data-mp="1shntesg7nm2j">
                      <div className="text-4xl mb-4" data-id="element-6346" data-mp="1eio73rg67j9j">{goal.emoji}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-6347" data-mp="1elk5fgvxk9yi">
                        {goal.title}
                      </h3>
                      <p className="text-gray-600" data-id="element-6348" data-mp="2pz5qoqyo5rer">{goal.description}</p>
                    </button>)}
                </div>
              </motion.div>}

            {step === 2 && <motion.div key="step2" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="space-y-6 pb-32" data-id="element-6349" data-mp="1gfwoaryyu59s">
                <div className="flex items-center justify-between mb-6" data-id="element-6350" data-mp="xjok7rhow0g2">
                  <button onClick={() => setStep(1)} className="flex items-center text-gray-600 hover:text-[#1E2B55]" data-id="element-6351" data-mp="11ixf4zogu4d7">
                    <ArrowLeftIcon className="w-5 h-5 mr-2" data-id="element-6352" data-mp="1bhleuicl1ws3" />
                    Back
                  </button>
                  <h2 className="text-2xl font-bold text-gray-900" data-id="element-6353" data-mp="2hyqhteuszu36">
                    Select Supplements
                  </h2>
                  <div className="w-20" data-id="element-6354" data-mp="2mvgxw26lypol"></div> {/* Spacer for centering */}
                </div>

                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6 flex items-start gap-3" data-id="element-6355" data-mp="b95b82bnnunl">
                  <div className="bg-blue-100 p-2 rounded-full text-blue-600 mt-0.5" data-id="element-6356" data-mp="6bdhr3vd40ha">
                    <ZapIcon className="w-5 h-5" data-id="element-6357" data-mp="1iam8q70ygyg" />
                  </div>
                  <div data-id="element-6358" data-mp="3sqy6f2tj3y4f">
                    <h4 className="font-semibold text-blue-900" data-id="element-6359" data-mp="3spn4l8jn90fy">
                      Bundle & Save 20%
                    </h4>
                    <p className="text-sm text-blue-700" data-id="element-6360" data-mp="3om8etq5wl5pb">
                      Select at least 2 supplements to unlock your bundle
                      discount.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6361" data-mp="39ru5yig1zcod">
                  {filteredSupplements.map(supp => {
                const isSelected = selectedSupplements.includes(supp.id);
                return <div key={supp.id} onClick={() => toggleSupplement(supp.id)} className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 flex gap-4 ${isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:border-green-200'}`} data-id="element-6362" data-mp="180h27wptnatw">
                        <div className="w-20 h-20 bg-white rounded-lg border border-gray-100 p-2 flex-shrink-0" data-id="element-6363" data-mp="2xi4c4l5b6s3g">
                          <img src={supp.image} alt={supp.name} className="w-full h-full object-contain" data-id="element-6364" data-mp="3l76szqs4cgww" />
                        </div>
                        <div className="flex-grow" data-id="element-6365" data-mp="3g7aq5uv5qvix">
                          <h3 className="font-bold text-gray-900 mb-1 leading-tight" data-id="element-6366" data-mp="3g5znjydg7tte">
                            {supp.name}
                          </h3>
                          <p className="text-sm text-gray-500 mb-2 line-clamp-2" data-id="element-6367" data-mp="24sx6713iz086">
                            {supp.description}
                          </p>
                          <div className="font-bold text-[#1E2B55]" data-id="element-6368" data-mp="1xazca4xttxq2">
                            SAR {supp.price.toFixed(2)}
                          </div>
                        </div>
                        <div className="absolute top-4 right-4" data-id="element-6369" data-mp="276as6qkz1aze">
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isSelected ? 'bg-green-500 border-green-500' : 'border-gray-300'}`} data-id="element-6370" data-mp="227pr6oxwan9w">
                            {isSelected && <CheckIcon className="w-4 h-4 text-white" data-id="element-6371" data-mp="1o7y8iawfoipk" />}
                          </div>
                        </div>
                      </div>;
              })}
                </div>

                {/* Floating Summary Bar */}
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] p-4 z-40" data-id="element-6372" data-mp="qt2cxve6pynn">
                  <div className="container mx-auto max-w-4xl flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-6373" data-mp="3q8edjyc2qqrb">
                    <div className="flex-grow flex items-center justify-between sm:justify-start sm:gap-8 w-full sm:w-auto" data-id="element-6374" data-mp="3u628v2hoiljq">
                      <div data-id="element-6375" data-mp="7tdiisz9velk">
                        <div className="text-sm text-gray-500" data-id="element-6376" data-mp="7tlwypb1mryv">
                          Selected Items
                        </div>
                        <div className="font-bold text-lg" data-id="element-6377" data-mp="cu17l93xxld5">
                          {selectedSupplements.length}
                        </div>
                      </div>
                      {selectedSupplements.length >= 2 && <>
                          <div data-id="element-6378" data-mp="qqeq8lk7nst7">
                            <div className="text-sm text-gray-500" data-id="element-6379" data-mp="qplpq2buhzji">
                              Original
                            </div>
                            <div className="font-medium text-gray-400 line-through" data-id="element-6380" data-mp="2sijdvkuog3y4">
                              SAR {originalTotal.toFixed(2)}
                            </div>
                          </div>
                          <div data-id="element-6381" data-mp="2g8k1um54qgi2">
                            <div className="text-sm text-green-600 font-medium" data-id="element-6382" data-mp="2g7im44d655u0">
                              Bundle Total
                            </div>
                            <div className="font-bold text-xl text-green-600" data-id="element-6383" data-mp="26bfb090c9wux">
                              SAR {discountedTotal.toFixed(2)}
                            </div>
                          </div>
                        </>}
                    </div>
                    <button onClick={() => setStep(3)} disabled={selectedSupplements.length < 2} className={`w-full sm:w-auto px-8 py-3 rounded-xl font-bold flex items-center justify-center transition-colors ${selectedSupplements.length >= 2 ? 'bg-[#1E2B55] text-white hover:bg-blue-900' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-6384" data-mp="2tzyk2qr7qdo2">
                      Continue to Review
                      <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-6385" data-mp="1fyk9rs8e6x0p" />
                    </button>
                  </div>
                </div>
              </motion.div>}

            {step === 3 && <motion.div key="step3" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="space-y-6" data-id="element-6386" data-mp="164to0v3nh0op">
                <div className="flex items-center justify-between mb-6" data-id="element-6387" data-mp="sb2g1r5khov3">
                  <button onClick={() => setStep(2)} className="flex items-center text-gray-600 hover:text-[#1E2B55]" data-id="element-6388" data-mp="sbc9joptme5j">
                    <ArrowLeftIcon className="w-5 h-5 mr-2" data-id="element-6389" data-mp="ibldvjpixt3v" />
                    Back
                  </button>
                  <h2 className="text-2xl font-bold text-gray-900" data-id="element-6390" data-mp="3vu8nv3pdwjod">
                    Review Bundle
                  </h2>
                  <div className="w-20" data-id="element-6391" data-mp="3qx8cqedogqqe"></div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-id="element-6392" data-mp="8qu0qkyyyqun">
                  <div className="p-6 border-b border-gray-100" data-id="element-6393" data-mp="3ommtpukx0po">
                    <h3 className="font-bold text-lg mb-4" data-id="element-6394" data-mp="imnda4coukcf">
                      Selected Supplements
                    </h3>
                    <div className="space-y-4" data-id="element-6395" data-mp="dnuab5v12bqi">
                      {selectedSupplements.map(id => {
                    const supp = mockSupplements.find(s => s.id === id);
                    if (!supp) return null;
                    const discountedPrice = supp.price * 0.8;
                    return <div key={supp.id} className="flex items-center gap-4" data-id="element-6396" data-mp="11blka2t3icom">
                            <div className="w-16 h-16 bg-gray-50 rounded-lg border border-gray-100 p-1 flex-shrink-0" data-id="element-6397" data-mp="23qrbwfxijeed">
                              <img src={supp.image} alt={supp.name} className="w-full h-full object-contain" data-id="element-6398" data-mp="27uz3qlz9pagt" />
                            </div>
                            <div className="flex-grow" data-id="element-6399" data-mp="1k4mkwg2hhtbg">
                              <h4 className="font-semibold text-gray-900" data-id="element-6400" data-mp="1p24y4injaup9">
                                {supp.name}
                              </h4>
                            </div>
                            <div className="text-right" data-id="element-6401" data-mp="1tzodjlvyxdis">
                              <div className="text-sm text-gray-400 line-through" data-id="element-6402" data-mp="1yszhnf9ebg70">
                                SAR {supp.price.toFixed(2)}
                              </div>
                              <div className="font-bold text-green-600" data-id="element-6403" data-mp="16b55n7gtz2r8">
                                SAR {discountedPrice.toFixed(2)}
                              </div>
                            </div>
                          </div>;
                  })}
                    </div>
                  </div>

                  <div className="p-6 bg-gray-50" data-id="element-6404" data-mp="osdcaxsezia4">
                    <div className="space-y-3 mb-6" data-id="element-6405" data-mp="jvmx0cofoqpr">
                      <div className="flex justify-between text-gray-600" data-id="element-6406" data-mp="jx7rk2ar09nm">
                        <span data-id="element-6407" data-mp="fubcglkf2vof">Original Total</span>
                        <span data-id="element-6408" data-mp="b334zv2ndkdw">SAR {originalTotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-green-600 font-medium" data-id="element-6409" data-mp="63q9klxpn4zp">
                        <span data-id="element-6410" data-mp="61dpo38e6kk0">Bundle Savings (20%)</span>
                        <span data-id="element-6411" data-mp="15yexk5ol4rt">- SAR {savings.toFixed(2)}</span>
                      </div>
                      <div className="h-px bg-gray-200 my-2" data-id="element-6412" data-mp="3s9viz1wgaj8y"></div>
                      <div className="flex justify-between text-xl font-bold text-gray-900" data-id="element-6413" data-mp="3o9lddqckluum">
                        <span data-id="element-6414" data-mp="2d5wu52l04ij1">Final Total</span>
                        <span data-id="element-6415" data-mp="2d4lqd2rk3arx">SAR {discountedTotal.toFixed(2)}</span>
                      </div>
                    </div>

                    <button onClick={handleAddToCart} className="w-full bg-[#1E2B55] text-white py-4 rounded-xl font-bold text-lg hover:bg-blue-900 transition-colors flex items-center justify-center" data-id="element-6416" data-mp="284piutjnh17m">
                      <ShoppingCartIcon className="w-5 h-5 mr-2" data-id="element-6417" data-mp="1u82ishwehuxh" />
                      Add Bundle to Cart
                    </button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </main>

        <Footer data-id="element-6418" data-mp="1gfw5r4ojo429" />
        <MobileFooterNav data-id="element-6419" data-mp="1gg5ukoxus56d" />
        <PharmacistChat data-id="element-6420" data-mp="1ggxev3m02ghc" />

        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={loc => {
        setSelectedLocation(loc);
        setLocationModalOpen(false);
      }} data-id="element-6421" data-mp="1gdjg05cjopen" />
      </div>
    </HelmetProvider>;
}